import { createFeatureSelector, createSelector } from '@ngrx/store';

import * as fromSealingElements from '../fromSealingElement.barrel';
import { SealingElementDetail } from 'src/app/models/sealing-element.model';

// feature
const featureState = createFeatureSelector<fromSealingElements.State>('sealingElements');

export const selectHasLoader = (loader: fromSealingElements.Loaders) => createSelector(featureState, state => state.loaders.includes(loader));

// lookups
export const selectPipeSizes = createSelector(featureState, state => state.pipeSizes);
export const selectMaterials = createSelector(featureState, state => state.materials);
export const selectEquipmentTypes = createSelector(featureState, state => state.equipmentTypes);
export const selectProductFamilies = createSelector(featureState, state => state.productFamilies);
export const selectSBUs = createSelector(featureState, state => state.sbus);
export const selectSubSBUs = createSelector(featureState, state => state.subSBUs);
export const selectSellables = createSelector(featureState, state => state.sellableStatuses);

export const selectSelectedPipesize = createSelector(featureState, selectPipeSizes, (state, pipesizes) => {
  if (!state.sealingElementFilter.pipeSizeID) return undefined;
  const pipeSize = pipesizes.find(f => f.id === state.sealingElementFilter.pipeSizeID);
  return pipeSize
});

// current filter
// the wall thickness will be used to calculate the innerdiameter if and only if the pipesizeid and wall thickness are given
// otherwise, we will just use the value for inner diameter
export const selectActiveFilter = createSelector(featureState, selectPipeSizes, (state, pipesizes) => {
  if (!state.sealingElementFilter.wallThickness || !state.sealingElementFilter.pipeSizeID) return state.sealingElementFilter;
  const pipeSize = pipesizes.find(f => f.id === state.sealingElementFilter.pipeSizeID);
  if (!pipeSize || !pipeSize.outsideDiameter) return state.sealingElementFilter;
  const innerDiameter = pipeSize.outsideDiameter - (state.sealingElementFilter.wallThickness * 2);
  return {
    ...state.sealingElementFilter,
    innerDiameter
  };
});

// we will be filtering down the plugging heads by the selected pipesizeID and equipmentTypeID
export const selectPluggingHeads = createSelector(featureState, state => state.pluggingHeads);
export const selectFilteredPluggingHeads = createSelector(selectPluggingHeads, selectActiveFilter, (pluggingHeads, activeFilter) => {
  const pipeSizeID = activeFilter.pipeSizeID ?? -1;
  const equipmentTypeID = activeFilter.equipmentTypeID ?? -1;

  return pluggingHeads
    .filter(f => (f.pipeSizeID === pipeSizeID || pipeSizeID === -1))
    .filter(f => (f.equipmentTypeID === equipmentTypeID || equipmentTypeID === -1))
});

export const selectFilteredPluggingHeadsCategories = createSelector(selectFilteredPluggingHeads, heads => heads.map(h => ({ ...h, description: `${h.description}  (${h.pluggingHeadPartNumber})` })));


export const selectActiveFilterHasValue = createSelector(selectActiveFilter,
  filt =>
    !!filt.region ||
    !!filt.sbuID ||
    !!filt.subSbuID ||
    !!filt.productFamilyID ||
    !!filt.equipmentTypeID ||
    !!filt.pipeSizeID ||
    !!filt.materialID ||
    !!filt.innerDiameter ||
    !!filt.pluggingHeadPartNumber ||
    (filt.sellableWHID || [])?.length > 0 ||
    (filt.sellableEHID || [])?.length > 0);

export const selectSealingElementsResponse = createSelector(featureState, state => state.sealingElements);
export const selectSealingElements = createSelector(selectActiveFilterHasValue, selectSealingElementsResponse, (filterHasValue, resp) => filterHasValue ? resp?.value : []);

export const selectSealingElementsCount = createSelector(selectActiveFilterHasValue, selectSealingElementsResponse, (filterHasValue, resp) => filterHasValue ? resp?.count : 0);

export const selectSkip = createSelector(featureState, state => state.sealingElements?.skip ?? 0);
export const selectTake = createSelector(featureState, state => state.sealingElements?.take ?? 50)
export const selectSealingElementsCurrentPage = createSelector(featureState, selectSkip, selectTake, (q, skip, take) => skip === 0 ? 1 : Math.ceil(((skip ?? 0) / (take ?? 50)) + 1));

export const selectFilteredSubSBUs = createSelector(selectActiveFilter, selectSubSBUs, (filt, subsbus) => {
  if (!filt.sbuID || filt.sbuID === '-1') return subsbus;
  return subsbus.filter(s => s.sbuID === filt.sbuID);
});

export const selectFilteredProductFamilies = createSelector(selectActiveFilter, selectProductFamilies,
  (filt, families) => {
    if (!filt.subSbuID) return families;
    return families.filter(f => f.subSBUID === filt.subSbuID);
  });

export const selectFilterEquipmentType = createSelector(selectEquipmentTypes, selectActiveFilter, (equipmentTypes, activeFilter) => {
  return equipmentTypes.find(e => e.id === activeFilter?.equipmentTypeID);
});

// selected product
const selectedProductPartNumber = createSelector(featureState, state => state.selectedProduct);
export const selectSelectedProduct = createSelector(selectSealingElements, selectedProductPartNumber, (els, pn) => (els ?? []).find(f => f.partNumber === pn));
export const selectSelectedProductDetail = createSelector(selectSelectedProduct, selectPipeSizes, selectMaterials, selectEquipmentTypes, selectProductFamilies, selectSBUs, selectSubSBUs, selectSellables,
  (product, sizes, materials, equipmentTypes, families, sbus, subsbus, sellables) => {
    const fam = families.find(f => f.id === product?.productFamilyID);
    const sbu = sbus.find(f => f.id === product?.sbuid);
    const subsbu = subsbus.find(f => f.subSbuID === product?.subSBUID);
    const sellWH = sellables.find(f => f.sellableID === product?.sellableWHID);
    const sellEH = sellables.find(f => f.sellableID === product?.sellableEHID)

    return {
      ...product,
      pipeSizeText: sizes.find(s => s.id === product?.nominalPipeSizeID)?.text,
      materialText: materials.find(m => m.id === product?.materialID)?.text,
      equipmentTypeText: equipmentTypes.find(e => e.id === product?.equipmentTypeID)?.text,
      productFamilyText: !!fam ? `${fam?.name} (${fam.id})` : '',
      sbuText: !!sbu ? `${sbu.name} (${sbu.id})` : '',
      subSBUText: !!subsbu ? `${subsbu.name} (${subsbu.subSbuID})` : '',
      sellableWHText: !!sellWH ? `${sellWH.description} (${sellWH.sellableID})` : '',
      sellableEHText: !!sellEH ? `${sellEH.description} (${sellEH.sellableID})` : ''
    } as SealingElementDetail;

  });

export const selectAnsiPipes = createSelector(featureState, state => state.ansiPipeSizes);

export const selectSealingElementsWithLookups = createSelector(selectSealingElements, selectPipeSizes, selectEquipmentTypes,
  (els, pipeSizes, equipmentTypes) => els?.map(el => ({
    ...el,
    pipeSize: pipeSizes.find(p => p.id === el.nominalPipeSizeID)?.text,
    equipmentType: equipmentTypes.find(e => e.id === el.equipmentTypeID)?.text
  })));

export const selectUIFilterQueryString = createSelector(selectActiveFilter, filter => {

  let q = '?';
  if (!!filter.region) q += `Region=${filter.region}&`;
  if (!!filter.sbuID) q += `SBUID=${filter.sbuID}&`;
  if (!!filter.subSbuID) q += `subSBUID=${filter.subSbuID}&`;
  if (!!filter.productFamilyID) q += `productFamilyID=${filter.productFamilyID}&`;
  if (!!filter.equipmentTypeID) q += `equipmentTypeID=${filter.equipmentTypeID}&`;
  if (!!filter.pipeSizeID) q += `pipeSizeID=${filter.pipeSizeID}&`;
  if (!!filter.materialID) q += `materialID=${filter.materialID}&`;
  if (!!filter.innerDiameter) q += `innerDiameter=${filter.innerDiameter}&`;
  if (!!filter.partNumber) q += `partNumber=${filter.partNumber.replace('%', '')}&`;
  if (!!filter.partNumberMatcher) q += `PartNumberMatcherSymbol=${filter.partNumberMatcher}&`;

  if (!!filter.itemName) q += `itemName=${filter.itemName}&`;
  if (!!filter.createdBy) q += `createdBy=${filter.createdBy}&`;
  if (!!filter.updatedBy) q += `updatedBy=${filter.updatedBy}&`;

  if (!!filter.pluggingHeadPartNumber) q += `pluggingHeadPartNumber=${filter.pluggingHeadPartNumber}&`;


  return q.substring(0, q.length - 1);
});
